import httpClient from "../http/http-client";
import {
  IArchivedAndReportPeriodDetails,
  IArchivedAndReportPeriodTableData,
  ICreateReportPeriod,
  IDeadlineReminder,
  IExchangeRateApplyJob,
  IExchangeRateApplyPreview,
  IExchangeRateMarketAverage,
  IPatchReportPeriodExchangeRate,
  IPostCloseReportPeriod,
  IPostReportPeriodExchangeRate,
  IReportPeriod,
  IReportPeriodExchangeRate,
  IReportPeriodExchangeRateList,
  IReportPeriodResponse,
} from './report-period.model';

export default class ReportPeriodService {
  http;

  constructor() {
    this.http = httpClient
  }

  async createReportPeriod(details: ICreateReportPeriod): Promise<IReportPeriodResponse> {
    return this.http.post('report-period', details)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err
      });
  }

  async getAllReportPeriod(queryString?: string): Promise<IReportPeriod[]> {
    return this.http.get(`report-period${queryString ? queryString : ''}`)
      .then(response => {
        return response.data.rows
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async getAllReportPeriodsWithReportDetailsPagination(includeReport: string, page: number, perPage: number = 10): Promise<{ count: number, rows: IReportPeriod[] }> {
    return this.http.get(`report-period?${includeReport}&page=${page}&perPage=${perPage}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err;
      });
  }

  async getReportPeriodAndArchivedReportPeriodsWithPagination(page: number, perPage: number = 10, orderBy?: string, order?: 'asc' | 'desc'): Promise<{count: number, rows: IArchivedAndReportPeriodTableData[]}> {
    return this.http.get(`report-period/get-report-and-archived?page=${page}&perPage=${perPage}${order && orderBy ? `&orderBy=${orderBy}&order=${order}` : ''}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }


  async getReportPeriodorArchivedReportPeriod(reportPeriodId: string): Promise<IArchivedAndReportPeriodDetails> {
    return this.http.get(`report-period/${reportPeriodId}/get-report-or-archived`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err
      });
  }

  async getBaseYear(): Promise<IArchivedAndReportPeriodDetails> {
    return this.http.get(`report-period/base-year`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err
      });
  }


  async updateBaseYear(reportPeriodId: string, manualEntry: boolean): Promise<any> {
    return this.http.post(`report-period/${reportPeriodId}/update-base-year`, {manualEntry: manualEntry})
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err
      });
  }

  async closeReportPeriod(reportPeriodId: string, values:IPostCloseReportPeriod): Promise<IReportPeriod[]> {
    return this.http.post(`report-period/${reportPeriodId}/close-period`, values)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }



  async publishGaiaReportWebpage(reportPeriodId: string): Promise<any> {
    return this.http.post(`report-period/${reportPeriodId}/gaia-publish`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async unpublishGaiaReportWebpage(reportPeriodId: string): Promise<any> {
    return this.http.post(`report-period/${reportPeriodId}/gaia-unpublish`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async updateLastSelectedDate(reportPeriodId: string): Promise<any> {
    return this.http.post(`report-period/${reportPeriodId}/selected`, {})
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async reopenReportPeriod(reportPeriodId: string): Promise<any> {
    return this.http.post(`report-period/${reportPeriodId}/reopen`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async deleteReportPeriod(reportPeriodId: string): Promise<any> {
    return this.http.delete(`report-period/${reportPeriodId}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async updateTotalRevenue(reportPeriodId: string, totalRevenue: number): Promise<any> {
    return this.http.post(`report-period/${reportPeriodId}/update-total-revenue`, { totalRevenue: totalRevenue })
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async updateDenominators(reportPeriodId: string, denominators: { totalRevenue?: number; numberOfUkEmployees?: number; numberOfNonUkEmployees?: number; balanceSheetTotal?: number | null }): Promise<any> {
    return this.http.post(`report-period/${reportPeriodId}/update-denominators`, denominators)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  /** The GHG report's performance commentary. Blank clears it; refused on a locked period. */
  async updatePerformanceCommentary(reportPeriodId: string, performanceCommentary: string | null): Promise<{ id: string; performanceCommentary: string | null }> {
    return this.http.patch(`report-period/${reportPeriodId}/commentary`, { performanceCommentary })
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async updateReportingDeadline(reportPeriodId: string, data: { reportingDeadline?: string | null; reminderDaysBefore?: number | null }): Promise<any> {
    return this.http.post(`report-period/${reportPeriodId}/update-reporting-deadline`, data)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async getDeadlineReminders(): Promise<IDeadlineReminder[]> {
    return this.http.get(`report-period/deadline-reminders`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async dismissDeadlineReminder(reportPeriodId: string): Promise<any> {
    return this.http.post(`report-period/${reportPeriodId}/dismiss-deadline-reminder`, {})
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async updateConversionYear(reportPeriodId: string, data: { conversionSourceId: string; newYear: string }): Promise<any> {
    return this.http.patch(`report-period/${reportPeriodId}/conversion-year`, data)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async getConversionYearRecalculationJobStatus(jobId: string): Promise<any> {
    return this.http.get(`report-period/conversion-year-job/${jobId}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async getConversionYearJobs(reportPeriodId: string): Promise<{ jobs: IConversionYearJob[] }> {
    return this.http.get(`report-period/${reportPeriodId}/conversion-year-jobs`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async getExchangeRates(reportPeriodId: string): Promise<IReportPeriodExchangeRateList> {
    return this.http.get(`report-period/${reportPeriodId}/exchange-rates`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async createExchangeRate(reportPeriodId: string, data: IPostReportPeriodExchangeRate): Promise<{ rate: IReportPeriodExchangeRate }> {
    return this.http.post(`report-period/${reportPeriodId}/exchange-rates`, data)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async updateExchangeRate(reportPeriodId: string, rateId: string, data: IPatchReportPeriodExchangeRate): Promise<{ rate: IReportPeriodExchangeRate }> {
    return this.http.patch(`report-period/${reportPeriodId}/exchange-rates/${rateId}`, data)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async deleteExchangeRate(reportPeriodId: string, rateId: string): Promise<any> {
    return this.http.delete(`report-period/${reportPeriodId}/exchange-rates/${rateId}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async getExchangeRateApplyPreview(reportPeriodId: string): Promise<IExchangeRateApplyPreview> {
    return this.http.get(`report-period/${reportPeriodId}/exchange-rates/apply/preview`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async applyExchangeRatesToExistingSpend(reportPeriodId: string): Promise<{ job: IExchangeRateApplyJob }> {
    return this.http.post(`report-period/${reportPeriodId}/exchange-rates/apply`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async getExchangeRateApplyJobs(reportPeriodId: string): Promise<{ jobs: IExchangeRateApplyJob[], reasonLabels: Record<string, string> }> {
    return this.http.get(`report-period/${reportPeriodId}/exchange-rates/apply/jobs`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async getExchangeRateApplyJob(reportPeriodId: string, jobId: string): Promise<IExchangeRateApplyJob> {
    return this.http.get(`report-period/${reportPeriodId}/exchange-rates/apply/jobs/${jobId}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async getExchangeRateMarketAverage(reportPeriodId: string, fromCurrencyCode: string, toCurrencyCode: string): Promise<IExchangeRateMarketAverage> {
    const query = `from=${encodeURIComponent(fromCurrencyCode)}&to=${encodeURIComponent(toCurrencyCode)}`
    return this.http.get(`report-period/${reportPeriodId}/exchange-rates/market-average?${query}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }
}

export interface IConversionYearJob {
  id: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  conversionSourceId: string;
  conversionSourceName: string | null;
  newYear: string;
  recalculatedCount: number;
  errorMessage: string | null;
  beforeTotals: { totalLocation?: number; totalMarket?: number } | null;
  afterTotals: { totalLocation?: number; totalMarket?: number } | null;
  createdAt: string;
  updatedAt: string;
}